import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Person } from '../ui/Avatar';
import { useAuth } from '../auth/auth-store';
import { formatDate } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  changeMemberRole,
  inviteMember,
  listMembers,
  listRoles,
  listTeams,
  removeMember,
  type MemberView,
  type RoleRow,
  type TeamRow,
} from './api';

/**
 * Roles, what each one can do, and the teams that bound record access.
 *
 * The question somebody has when they open this screen is "what can a SALES_REP actually see", so
 * it answers with the permissions and their record scope rather than with a list of names — and
 * the role on each person's row is a control, not a label.
 */
const MEMBER_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  INVITED: 'warning',
  SUSPENDED: 'danger',
  REMOVED: 'neutral',
};

/**
 * The people in the tenant, and the form that adds one.
 *
 * An invitation is visible as a member in `INVITED` state rather than hidden in a separate list:
 * "who is on my team" and "who have I asked" are the same question, and splitting them means
 * somebody invites the same colleague twice.
 */
function Members(): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();

  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('SALES_REP');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const roles = useQuery({ queryKey: ['roles'], queryFn: listRoles });

  const invite = useMutation({
    mutationFn: () => inviteMember({ email: email.trim(), roleKey }),
    onSuccess: (member) => {
      setEmail('');
      setError(null);
      setSent(member.email);
      void client.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (caught: unknown) => {
      setSent(null);
      setError(messageFor(caught));
    },
  });

  const changeRole = useMutation({
    mutationFn: (input: { membershipId: string; roleKey: string }) =>
      changeMemberRole(input.membershipId, input.roleKey),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /** The role's display name, falling back to its key for a role this list does not carry. */
  const roleName = (key: string): string =>
    roles.data?.roles.find((role) => role.key === key)?.name ?? key;

  const remove = useMutation({
    mutationFn: (membershipId: string) => removeMember(membershipId),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<MemberView>[] = [
    {
      key: 'person',
      header: t('team.person'),
      // The one list where "who is here right now" is the question somebody is asking.
      render: (member) => (
        <Person
          name={member.name}
          url={member.avatar_url}
          size="md"
          secondary={member.email}
          userId={member.user_id}
        />
      ),
    },
    {
      key: 'role',
      header: t('team.role'),
      /*
       * Editable in place, because "what is this person allowed to do" is the reason somebody
       * opens this screen, and reading it back as a code they cannot change is the reason they
       * leave it. Your own row stays read-only: demoting yourself here is a door that locks from
       * the inside.
       */
      render: (member) =>
        member.user_id === user?.id ? (
          <code>{roleName(member.role_key)}</code>
        ) : (
          <select
            className="select select--compact"
            value={member.role_key}
            disabled={changeRole.isPending}
            aria-label={t('team.role')}
            onChange={(event) => {
              changeRole.mutate({
                membershipId: member.membership_id,
                roleKey: event.target.value,
              });
            }}
          >
            {(roles.data?.roles ?? [])
              .filter((role) => role.is_system)
              .map((role) => (
                <option key={role.id} value={role.key}>
                  {role.name}
                </option>
              ))}
          </select>
        ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (member) => (
        <span className="cell-stack">
          <Badge tone={MEMBER_TONE[member.status] ?? 'neutral'}>
            {t(`team.member.${member.status}`)}
          </Badge>
          {member.status === 'INVITED' && member.expires_at && (
            <span className="muted small">
              {t('team.inviteExpires').replace('{date}', formatDate(member.expires_at))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'joined',
      header: t('team.joined'),
      secondary: true,
      render: (member) => formatDate(member.joined_at),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (member) =>
        // Removing your own access from here would lock you out of the screen you did it on.
        member.user_id === user?.id ? (
          <span className="muted small">{t('team.you')}</span>
        ) : (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={remove.isPending}
            onClick={() => remove.mutate(member.membership_id)}
          >
            {member.status === 'INVITED' ? t('team.cancelInvite') : t('team.removeAccess')}
          </button>
        ),
    },
  ];

  return (
    <Panel title={t('team.people')}>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) invite.mutate();
        }}
      >
        <label className="field-search">
          <input
            type="email"
            value={email}
            placeholder={t('team.invitePlaceholder')}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <select
          className="select"
          value={roleKey}
          onChange={(event) => setRoleKey(event.target.value)}
          aria-label={t('team.role')}
        >
          {(roles.data?.roles ?? []).map((role) => (
            <option key={role.id} value={role.key}>
              {role.name}
            </option>
          ))}
        </select>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={email.trim() === '' || invite.isPending}
        >
          {invite.isPending ? t('team.inviting') : t('team.invite')}
        </button>
      </form>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {sent && (
        <p className="notice" role="status">
          {t('team.inviteSent').replace('{email}', sent)}
        </p>
      )}

      <DataTable
        tableKey="team"
        rows={members.data?.members}
        columns={columns}
        rowKey={(member) => member.membership_id}
        isLoading={members.isLoading}
        error={members.error}
        onRetry={() => void members.refetch()}
        empty={{ title: t('team.noMembers') }}
      />
    </Panel>
  );
}

export function TeamPage(): React.JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: listRoles });
  const teams = useQuery({ queryKey: ['teams'], queryFn: listTeams });

  const roleColumns: Column<RoleRow>[] = [
    {
      key: 'name',
      header: t('team.role'),
      render: (role) => (
        <span className="cell-stack">
          <span className="strong">{role.name}</span>
          <span className="muted small">{role.description ?? role.key}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('team.kind'),
      secondary: true,
      render: (role) =>
        role.is_system ? (
          <Badge tone="neutral">{t('team.system')}</Badge>
        ) : (
          <Badge tone="info">{t('team.custom')}</Badge>
        ),
    },
    {
      key: 'permissions',
      header: t('team.permissions'),
      align: 'end',
      render: (role) => String(role.permissions.length),
    },
  ];

  const teamColumns: Column<TeamRow>[] = [
    {
      key: 'name',
      header: t('team.team'),
      render: (team) => <span className="strong">{team.name}</span>,
    },
    {
      key: 'parent',
      header: t('team.parent'),
      secondary: true,
      render: (team) => team.parent_id ?? '—',
    },
  ];

  const role = roles.data?.roles.find((entry) => entry.id === expanded);

  return (
    <div className="view">
      <PageHeader title={t('nav.team')} description={t('team.description')} />

      <Members />

      <Panel title={t('team.roles')}>
        <DataTable
          tableKey="team-2"
          rows={roles.data?.roles}
          columns={roleColumns}
          rowKey={(entry) => entry.id}
          isLoading={roles.isLoading}
          error={roles.error}
          onRetry={() => void roles.refetch()}
          onRowClick={(entry) => setExpanded((current) => (current === entry.id ? null : entry.id))}
          empty={{ title: t('team.noRoles') }}
        />
      </Panel>

      {role && (
        <Panel title={t('team.whatCan').replace('{role}', role.name)}>
          <ul className="permission-grid">
            {[...role.permissions]
              .sort((a, b) => a.key.localeCompare(b.key))
              .map((permission) => (
                <li key={permission.key}>
                  <code>{permission.key}</code>
                  <Badge tone={permission.scope === 'ALL' ? 'info' : 'neutral'}>
                    {t(`scope.${permission.scope}`)}
                  </Badge>
                </li>
              ))}
          </ul>
        </Panel>
      )}

      <Panel title={t('team.teams')}>
        <DataTable
          tableKey="team-3"
          rows={teams.data?.teams}
          columns={teamColumns}
          rowKey={(team) => team.id}
          isLoading={teams.isLoading}
          error={teams.error}
          onRetry={() => void teams.refetch()}
          empty={{ title: t('team.noTeams'), description: t('team.noTeamsBody') }}
        />
      </Panel>
    </div>
  );
}
